import { ValueFormatterParams } from 'ag-grid-community';
import { memoize } from 'lodash';
import moment from 'moment';
import { humanise } from 'components/atoms/humanised-date';
import { formatter } from 'components/atoms/input-duration';
import { placeholderForEmpty } from 'components/atoms/table/formatters/empty-value-formatter';
import { Field } from 'stores/fields';
import { unreachable } from 'utils/unreachable';
import { FieldType } from '../fields';

type Nullable<T> = T | null;

interface ValueFormatter {
  (params: ValueFormatterParams): string;
}

const formatNumber = memoize((value: Nullable<number>) =>
  value == null ? placeholderForEmpty : value.toString(),
);
const formatEffortAsDuration = memoize((value: Nullable<number>) =>
  value == null ? placeholderForEmpty : `${formatter(value)}`,
);

export const formatDate = memoize((value: Nullable<number>) =>
  value == null ? placeholderForEmpty : humanise(moment(value)),
);
const formatText = memoize((value: Nullable<string>) =>
  value == null ? placeholderForEmpty : value,
);

export const getValueFormatter = (
  field: Pick<Field, 'type' | 'fieldName'>,
): ValueFormatter | undefined => {
  const { type } = field;

  switch (type) {
    case FieldType.Number:
      return ['effort', 'timeEntryMinutes'].includes(field.fieldName)
        ? ({ value }: { value: Nullable<number> }) =>
            formatEffortAsDuration(value)
        : ({ value }: { value: Nullable<number> }) => formatNumber(value);
    case FieldType.Date:
      return ({ value }: { value: Nullable<number> }) => formatDate(value);
    case FieldType.Text:
    case FieldType.MultiLineText:
      return ({ value }: { value: Nullable<string> }) => formatText(value);
    case FieldType.Boolean:
    case FieldType.SingleSelect:
    case FieldType.MultiSelect:
    case FieldType.SingleUser:
    case FieldType.MultiUser:
    case FieldType.Assignee:
    case FieldType.Estimate: // estimate is not an actual custom field type. In future if it is needed, please handle the case accordingly.
      return undefined;
    default:
      return unreachable(type);
  }
};

export const getValueFormatterBasePivot = (
  field: Pick<Field, 'type' | 'fieldName'>,
  value,
) => {
  const { type } = field;

  switch (type) {
    case FieldType.Number:
      return ['effort', 'timeEntryMinutes'].includes(field.fieldName)
        ? formatEffortAsDuration(value)
        : formatNumber(value);
    case FieldType.Date:
      return formatDate(value);
    case FieldType.Text:
    case FieldType.MultiLineText:
      return formatText(value);
    case FieldType.Boolean:
    case FieldType.SingleSelect:
    case FieldType.MultiSelect:
    case FieldType.SingleUser:
    case FieldType.MultiUser:
    case FieldType.Assignee:
    case FieldType.Estimate: // estimate is not an actual custom field type. In future if it is needed, please handle the case accordingly.
      return undefined;
    default:
      return unreachable(type);
  }
};
